import React, { useState } from "react";
import Layout from "../Components/Layout";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getAllPackages } from "../redux/actions/packageActions";
import { getAllCallPackages } from "../redux/actions/callPackageActions";
import { useNavigate } from "react-router-dom";
import useCallOnce from "../hooks/useCallOnce";

function AllPackages() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading, pagination } = useSelector((s) => s.packages || { list: [], loading: false, pagination: {} });
  const { list: callPackagesList, loading: callPackagesLoading, pagination: callPackagesPagination } = useSelector((s) => s.callPackages || { list: [], loading: false, pagination: {} });

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [filters, setFilters] = useState({
    status: "",
    company: "",
    email: "",
    packageName: "",
    packageCycle: "",
    startDate: "",
    endDate: "",
  });

  const fetchList = (override = {}, filtersOverride) => {
    const apiPage = (override.page ?? page) + 1;
    const f = { ...filters, ...(filtersOverride || {}) };
    dispatch(
      getAllPackages({
        status: f.status || undefined,
        company: f.company || undefined,
        email: f.email || undefined,
        packageName: f.packageName || undefined,
        packageCycle: f.packageCycle || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
        page: apiPage,
        limit: override.limit ?? rowsPerPage,
      })
    );
  };

  const fetchCallPackages = (override = {}, filtersOverride) => {
    const apiPage = (override.page ?? page) + 1;
    const f = { ...filters, ...(filtersOverride || {}) };
    dispatch(
      getAllCallPackages({
        status: f.status || undefined,
        planName: f.packageName || undefined,
        interval: f.packageCycle || undefined,
        company: f.company || undefined,
        email: f.email || undefined,
        fromDate: f.startDate || undefined,
        toDate: f.endDate || undefined,
        page: apiPage,
        limit: override.limit ?? rowsPerPage,
      })
    );
  };

  // Avoid duplicate initial fetches in React 18 StrictMode
  useCallOnce("packages/initial-load", () => {
    fetchList({ page: 0 });
  }, [rowsPerPage]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
    // Clear filters when switching tabs
    const resetFilters = { status: "", company: "", email: "", packageName: "", packageCycle: "", startDate: "", endDate: "" };
    setFilters(resetFilters);
    
    if (newValue === 0) {
      fetchList({ page: 0 }, resetFilters);
    } else {
      fetchCallPackages({ page: 0 }, resetFilters);
    }
  };

  const handleChangePage = (_e, newPage) => {
    setPage(newPage);
    if (activeTab === 0) {
      fetchList({ page: newPage });
    } else {
      fetchCallPackages({ page: newPage });
    }
  };

  const handleChangeRowsPerPage = (e) => {
    const newRows = parseInt(e.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    if (activeTab === 0) {
      fetchList({ page: 0, limit: newRows });
    } else {
      fetchCallPackages({ page: 0, limit: newRows });
    }
  };

  const rows = activeTab === 0 
    ? (Array.isArray(list) ? list : (list?.items || []))
    : (Array.isArray(callPackagesList) ? callPackagesList : (callPackagesList?.items || []));
  
  const currentLoading = activeTab === 0 ? loading : callPackagesLoading;
  const currentPagination = activeTab === 0 ? pagination : callPackagesPagination;

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            All Packages
          </Typography>
          {/* <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Search and filter packages
          </Typography> */}
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 2 }}>
            <Tab label="Chat Packages" />
            <Tab label="Call Packages" />
          </Tabs>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Filters ordered to match table columns: Start Date, End Date, Company, Email, Package Name/Plan Name, Package Cycle/Plan Interval, Status */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.startDate}
                onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters.endDate}
                onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Company"
                value={filters.company}
                onChange={(e) => setFilters((f) => ({ ...f, company: e.target.value }))}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={filters.email}
                onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={activeTab === 0 ? "Package Name" : "Plan Name"}
                value={filters.packageName}
                onChange={(e) => setFilters((f) => ({ ...f, packageName: e.target.value }))}
              />
            </Grid>

            {/* Package Cycle Filter - Only for Chat Packages */}
            {activeTab === 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="package-cycle-label">Package Cycle</InputLabel>
                  <Select
                    labelId="package-cycle-label"
                    label="Package Cycle"
                    value={filters.packageCycle}
                    onChange={(e) => setFilters((f) => ({ ...f, packageCycle: e.target.value }))}
                  >
                    {/* <MenuItem value="">All</MenuItem> */}
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Plan Interval Filter - Only for Call Packages */}
            {activeTab === 1 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="plan-interval-label">Plan Interval</InputLabel>
                  <Select
                    labelId="plan-interval-label"
                    label="Plan Interval"
                    value={filters.packageCycle}
                    onChange={(e) => setFilters((f) => ({ ...f, packageCycle: e.target.value }))}
                  >
                    {/* <MenuItem value="">All</MenuItem> */}
                    <MenuItem value="month">Month</MenuItem>
                    <MenuItem value="year">Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  label="Status"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  {activeTab === 0 && <MenuItem value="deactive">Deactive</MenuItem>}
                  {activeTab === 1 && <MenuItem value="canceled">Canceled</MenuItem>}
                </Select>
              </FormControl>
            </Grid>

            {/* End moved date fields */}

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setPage(0);
                    if (activeTab === 0) {
                      fetchList({ page: 0 });
                    } else {
                      fetchCallPackages({ page: 0 });
                    }
                  }}
                  sx={{ textTransform: "none", bgcolor: "#111827", "&:hover": { bgcolor: "#0b1220" } }}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const reset = { status: "", company: "", email: "", packageName: "", packageCycle: "", startDate: "", endDate: "" };
                    setFilters(reset);
                    setPage(0);
                    // Use override filters to ensure API receives cleared values immediately
                    if (activeTab === 0) {
                      fetchList({ page: 0 }, reset);
                    } else {
                      fetchCallPackages({ page: 0 }, reset);
                    }
                  }}
                  sx={{ textTransform: "none", borderColor: "#111827", color: "#111827" }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>S. No</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Email</TableCell>
                {activeTab === 0 ? (
                  <>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Package</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Cycle</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Status</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Plan Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Plan Price</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Plan Interval</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Plan Features</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Days Until Renewal</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Status</TableCell>
                     <TableCell sx={{ fontWeight: 600, bgcolor: "#f8fafc" }}>Cancel at Period End</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentLoading ? (
                <TableRow>
                  <TableCell colSpan={activeTab === 0 ? 6 : 10} sx={{ textAlign: "center", py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((item, index) => (
                  <TableRow
                    key={item._id || item.id || index}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      const id = item._id || item.id;
                      const pkgName = (item.package && (item.package.name || item.package.package_name)) || item.packageName || item.planName || '';
                      if (id) {
                        navigate(`/package-logs`, { state: { userId: id, packageName: pkgName } });
                      }
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{item.company || '-'}</TableCell>
                    <TableCell>{item.email || '-'}</TableCell>
                    {activeTab === 0 ? (
                      <>
                        <TableCell>{(item.package && (item.package.name || item.package.package_name)) || item.packageName || '-'}</TableCell>
                        <TableCell>{(item.package && item.package.cycle) || item.packageCycle || '-'}</TableCell>
                        <TableCell>{item.status || '-'}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{item.planName || '-'}</TableCell>
                        <TableCell>{item.planPrice ? `$${(item.planPrice / 100).toFixed(2)}` : '-'}</TableCell>
                        <TableCell>{item.planInterval || '-'}</TableCell>
                        <TableCell>
                          {item.planFeatures && Array.isArray(item.planFeatures) 
                            ? item.planFeatures.join(', ') 
                            : item.planFeatures || '-'}
                        </TableCell>
                        <TableCell>{item.daysUntilRenewal || '-'}</TableCell>
                        <TableCell>{item.status || '-'}</TableCell>
                        <TableCell>
                          {item.cancelAtPeriodEnd === true ? 'Yes' : '-'}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={activeTab === 0 ? 6 : 10} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                      No packages found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={currentPagination.totalCount || rows.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Layout>
  );
}

export default AllPackages;


